/**
 * SHA-256 hash implementation — exact clone of common.sha256() from common.js.
 * Pure JavaScript, no dependencies. Used for entity identity hashing and dynamic API caching.
 */
export function sha256(str: string): string {
  const maxWord = 2 ** 32;
  let result = '';

  const words: number[] = [];
  const asciiBitLength = str.length * 8;

  const hash: number[] = (sha256 as unknown as { h?: number[] }).h || [];
  const k: number[] = (sha256 as unknown as { k?: number[] }).k || [];

  let primeCounter = k.length;

  const isComposite: Record<number, boolean> = {};

  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (!isComposite[candidate]) {
      for (let i = 0; i < 313; i += candidate) {
        isComposite[i] = true;
      }
      hash[primeCounter] = (Math.pow(candidate, 0.5) * maxWord) | 0;
      k[primeCounter++] = (Math.pow(candidate, 1 / 3) * maxWord) | 0;
    }
  }

  // Cache for reuse
  (sha256 as unknown as { h: number[]; k: number[] }).h = hash;
  (sha256 as unknown as { k: number[] }).k = k;

  const strToProcess = str + '\x80';
  for (let i = 0; i < strToProcess.length; i++) {
    const code = strToProcess.charCodeAt(i);
    if (code >> 8) return ''; // ASCII only
    words[i >> 2] |= code << (((3 - i) % 4) * 8);
  }

  // SHA-256 padding: length must be at the END of the final 512-bit (16-word) block.
  // Calculate how many 16-word blocks we need: data + 0x80 + 8 bytes for length.
  const totalBytesWithPad = str.length + 1 + 8; // original + 0x80 + 64-bit length
  const neededWords = Math.ceil(totalBytesWithPad / 64) * 16;

  // Zero-fill to the required length (Array.slice won't pad beyond array bounds)
  while (words.length < neededWords) {
    words.push(0);
  }

  // Place 64-bit big-endian length at the last two words of the final block
  words[neededWords - 2] = (asciiBitLength / maxWord) | 0;
  words[neededWords - 1] = asciiBitLength;

  const hashCopy = hash.slice(0, 8);

  for (let j = 0; j < words.length; ) {
    const w = words.slice(j, (j += 16));
    const oldHash = hashCopy.slice(0);

    for (let i = 0; i < 64; i++) {
      const w15 = w[i - 15];
      const w2 = w[i - 2];

      const a = hashCopy[0];
      const e = hashCopy[4];

      const temp1 =
        hashCopy[7] +
        (((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7))) +
        ((e & hashCopy[5]) ^ (~e & hashCopy[6])) +
        k[i] +
        (w[i] =
          i < 16
            ? w[i]
            : (w[i - 16] +
                (((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3)) +
                w[i - 7] +
                (((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10))) |
              0);

      const temp2 =
        (((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10))) +
        ((a & hashCopy[1]) ^ (a & hashCopy[2]) ^ (hashCopy[1] & hashCopy[2]));

      hashCopy.pop();
      hashCopy.unshift((temp1 + temp2) | 0);
      hashCopy[4] = (hashCopy[4] + temp1) | 0;
    }

    for (let i = 0; i < 8; i++) {
      hashCopy[i] = (hashCopy[i] + oldHash[i]) | 0;
    }
  }

  for (let i = 0; i < 8; i++) {
    for (let j = 3; j >= 0; j--) {
      const b = (hashCopy[i] >> (j * 8)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }

  return result;
}
